import React, { useMemo, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Empty, Notice, Label } from "./ui/index.jsx";
import { InlineEdit, inputStyle } from "./CellEdit.jsx";
import { describeImpact } from "../lib/schedule/impact.js";
import { fmtDate } from "../lib/format.js";
import { useAuth } from "../auth/AuthProvider.jsx";

/* The schedule, live and editable, while a meeting is happening.
 *
 * The thing this is optimised for is one job: a vendor says an activity has
 * moved, and it goes in, and the room finds out immediately what that costs.
 * So an edit saves straight away and the consequence appears as a sentence,
 * with an undo. No draft mode, no apply button, nothing to forget to press
 * while somebody is talking.
 *
 * 555 rows is a lot to look at and a filter is not optional. Everything here
 * narrows: the box, the view, and collapsing a section you are not discussing.
 */

const cell = {
  padding: "4px 7px", fontSize: 12.5, borderBottom: `1px solid ${T.line}`,
  verticalAlign: "top", textAlign: "left",
};
const head = {
  ...cell, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: T.navy, borderBottom: `2px solid ${T.navy}`,
  whiteSpace: "nowrap", position: "sticky", top: 0, background: T.panel, zIndex: 1,
};

const VIEWS = [
  ["all", "Everything"],
  ["critical", "Critical path"],
  ["negative", "Behind their own logic"],
  ["progress", "In progress"],
  ["open", "Not finished"],
  ["loose", "Nothing tied to them"],
];

/* Float, said the way a person reads it. A big number is not interesting and
   a small one is, so the colour is on the small ones. */
function Float({ days, critical }) {
  if (days === null || days === undefined) return <span style={{ color: T.soft }}>—</span>;
  const colour = days < 0 ? "#8F2B25" : days === 0 ? GOLD_INK : days <= 10 ? T.warn : T.soft;
  return (
    <span style={{ color: colour, fontWeight: days <= 10 ? 700 : 400, whiteSpace: "nowrap" }}>
      {days < 0 ? `${days}d late` : critical ? "critical" : `${days}d`}
    </span>
  );
}

/* Choosing what comes before an activity. A search box rather than a dropdown
   of 555 rows, because nobody scrolls to find A2340 and everybody can type
   part of its name. */
function AddPredecessor({ rows, row, onPick, onClose }) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("FS");
  const [lag, setLag] = useState(0);

  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return rows
      .filter((r) => r.id !== row.id && !r.is_summary)
      .filter((r) => `${r.title} ${r.activity_code ?? ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [rows, row, query]);

  return (
    <div style={{
      position: "absolute", zIndex: 20, right: 0, marginTop: 4, width: 330,
      background: T.panel, border: `1px solid ${T.navy}`, borderRadius: 4,
      boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 10,
    }}>
      <div style={{ fontSize: 11.5, color: T.soft, marginBottom: 6 }}>
        What has to happen before <b style={{ color: T.navy }}>{row.title.slice(0, 40)}</b>?
      </div>
      <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Type part of its name or code"
        style={{ ...inputStyle, padding: "5px 8px", fontSize: 12.5 }} />

      <div style={{ display: "flex", gap: 6, margin: "7px 0" }}>
        <select value={type} onChange={(e) => setType(e.target.value)}
          style={{ ...inputStyle, width: "auto", padding: "4px 6px", fontSize: 12 }}>
          <option value="FS">finish to start</option>
          <option value="SS">start together</option>
          <option value="FF">finish together</option>
          <option value="SF">start to finish</option>
        </select>
        <input type="number" value={lag} onChange={(e) => setLag(Number(e.target.value) || 0)}
          title="Days of gap between them"
          style={{ ...inputStyle, width: 70, padding: "4px 6px", fontSize: 12 }} />
        <span style={{ fontSize: 11.5, color: T.soft, alignSelf: "center" }}>days gap</span>
      </div>

      <div style={{ maxHeight: 190, overflowY: "auto" }}>
        {found.map((r) => (
          <button key={r.id}
            onClick={() => onPick({ from: r.id, to: row.id, link_type: type, lag_days: lag })}
            style={{
              display: "block", width: "100%", textAlign: "left", border: "none",
              background: "transparent", cursor: "pointer", padding: "5px 6px",
              font: "inherit", fontSize: 12, borderRadius: 3, color: T.ink,
            }}>
            <span style={{ color: T.soft }}>{r.activity_code}</span>{" "}{r.title.slice(0, 44)}
          </button>
        ))}
        {query.trim() && !found.length && (
          <div style={{ fontSize: 12, color: T.soft, padding: "5px 6px" }}>Nothing matches that.</div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
        <Btn small kind="ghost" onClick={onClose}>Close</Btn>
      </div>
    </div>
  );
}

export default function ScheduleTable({ contract, schedule }) {
  const { canEdit } = useAuth();
  const [query, setQuery] = useState("");
  const [view, setView] = useState("all");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [note, setNote] = useState(null);
  const [undo, setUndo] = useState(null);
  const [linking, setLinking] = useState(null);
  const [picked, setPicked] = useState(() => new Set());

  const rows = schedule.computed?.activities ?? [];
  const links = schedule.links ?? [];

  const predecessorsOf = useMemo(() => {
    const by = new Map();
    for (const l of links) {
      if (!by.has(l.to)) by.set(l.to, []);
      by.get(l.to).push(l);
    }
    return by;
  }, [links]);

  const successorCount = useMemo(() => {
    const by = new Map();
    for (const l of links) by.set(l.from, (by.get(l.from) ?? 0) + 1);
    return by;
  }, [links]);

  /* A row is hidden when any of its ancestors is collapsed, which is what
     makes a plan this size navigable at all. */
  const hidden = useMemo(() => {
    if (!collapsed.size) return new Set();
    const byId = new Map(rows.map((r) => [r.id, r]));
    const out = new Set();
    for (const r of rows) {
      let at = r.parent_id;
      while (at) {
        if (collapsed.has(at)) { out.add(r.id); break; }
        at = byId.get(at)?.parent_id;
      }
    }
    return out;
  }, [rows, collapsed]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (hidden.has(r.id)) return false;
      if (q && !`${r.title} ${r.activity_code ?? ""} ${r.activity_id ?? ""}`.toLowerCase().includes(q)) {
        return false;
      }
      switch (view) {
        case "critical": return r.is_critical && !r.is_summary;
        case "negative": return (r.total_float ?? 0) < 0;
        case "progress": return (r.percent_complete ?? 0) > 0 && (r.percent_complete ?? 0) < 100;
        case "open": return (r.percent_complete ?? 0) < 100 && !r.is_summary;
        case "loose": return !r.is_summary && !predecessorsOf.has(r.id) && !successorCount.has(r.id);
        default: return true;
      }
    });
  }, [rows, query, view, hidden, predecessorsOf, successorCount]);

  /* Save, then say what it did. The sentence is the point: a table of forty
     changed rows does not tell anybody in a meeting whether this matters. */
  const change = async (row, patch) => {
    const before = Object.fromEntries(Object.keys(patch).map((k) => [k, row[k]]));
    const impact = schedule.preview({ [row.id]: patch });

    if (impact && !impact.ok) {
      setNote({ kind: "error", text: describeImpact(impact) });
      return false;
    }

    const saved = await schedule.saveActivity({ ...row, ...patch });
    if (!saved) return false;

    setNote({ kind: impact?.summary?.projectSlip > 0 ? "warn" : "ok", text: describeImpact(impact) });
    setUndo({ row, before });
    return true;
  };

  /* Tie a run of activities end to end, in the order they are shown. This is
     how 300 loose activities actually get fixed: the work comes in chains,
     and the three island sequences in this plan are the same five steps over
     and over. Doing it one link at a time would take an afternoon. */
  const chainSelected = async () => {
    const order = shown.filter((r) => picked.has(r.id) && !r.is_summary);
    if (order.length < 2) return;
    const links = [];
    for (let i = 1; i < order.length; i += 1) {
      links.push({ from: order[i - 1].id, to: order[i].id, link_type: "FS", lag_days: 0 });
    }
    const ok = await schedule.addLinks(links);
    if (!ok) return;
    setPicked(new Set());
    setNote({
      kind: "ok",
      text: `Tied ${order.length} activities end to end, ${links.length} link${links.length === 1 ? "" : "s"}. ` +
            `Each one now starts when the one before it finishes.`,
    });
  };

  const takeItBack = async () => {
    if (!undo) return;
    await schedule.saveActivity({ ...undo.row, ...undo.before });
    setUndo(null);
    setNote({ kind: "info", text: "Put back the way it was." });
  };

  if (!schedule.computed) return <Empty text="Loading the schedule..." />;
  if (!rows.length) {
    return <Empty text="No schedule yet. Import a plan on the Import page and it appears here." />;
  }

  const criticalCount = rows.filter((r) => r.is_critical && !r.is_summary).length;
  const looseCount = rows.filter((r) => !r.is_summary
    && !predecessorsOf.has(r.id) && !successorCount.has(r.id)).length;

  return (
    <Panel
      title="Activities"
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {schedule.live && <Chip color={T.ok}>Live</Chip>}
          <select value={view} onChange={(e) => setView(e.target.value)}
            style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12.5 }}>
            {VIEWS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or code"
            style={{ ...inputStyle, width: 200, padding: "5px 8px", fontSize: 12.5 }} />
        </div>
      }
    >
      {schedule.error && <div style={{ marginBottom: 10 }}><Notice kind="error">{schedule.error}</Notice></div>}

      {!schedule.computed.ok && (
        <div style={{ marginBottom: 10 }}>
          <Notice kind="error">
            The logic contains a loop, so no dates can be worked out.
            {" "}{schedule.computed.cycle.length} activities are in it. Until it is
            broken, everything below is showing the dates as they were entered.
          </Notice>
        </div>
      )}

      {note && (
        <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1 }}><Notice kind={note.kind}>{note.text}</Notice></div>
          {undo && <Btn small kind="ghost" onClick={takeItBack}>Undo</Btn>}
          <Btn small kind="ghost" onClick={() => { setNote(null); setUndo(null); }}>Dismiss</Btn>
        </div>
      )}

      {picked.size > 0 && (
        <div style={{
          display: "flex", gap: 8, alignItems: "center", marginBottom: 10,
          padding: "8px 11px", background: T.chip, borderRadius: 4,
          border: `1px solid ${T.navy}`, flexWrap: "wrap",
        }}>
          <b style={{ fontSize: 13, color: T.navy }}>{picked.size} picked</b>
          <span style={{ fontSize: 12, color: T.soft, flex: 1, minWidth: 200 }}>
            Tying them end to end makes each one start when the one before it
            finishes, in the order shown here.
          </span>
          <Btn small kind="gold" disabled={picked.size < 2} onClick={chainSelected}>
            Tie {picked.size} end to end
          </Btn>
          <Btn small kind="ghost" onClick={() => setPicked(new Set())}>Clear</Btn>
        </div>
      )}

      <div style={{ fontSize: 12, color: T.soft, marginBottom: 8, lineHeight: 1.6 }}>
        {shown.length === rows.length ? `${rows.length} rows` : `${shown.length} of ${rows.length} rows`}
        {" · "}{criticalCount} on the critical path
        {looseCount > 0 && (
          <>
            {" · "}
            <span style={{ color: GOLD_INK }}>
              {looseCount} tied to nothing, so nothing moves them and they move nothing
            </span>
          </>
        )}
      </div>

      <div style={{ overflow: "auto", maxHeight: "68vh" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {canEdit && <th style={{ ...head, width: 1 }} />}
              <th style={head}>Code</th>
              <th style={head}>Activity</th>
              <th style={head}>Start</th>
              <th style={head}>Finish</th>
              <th style={{ ...head, textAlign: "right" }}>%</th>
              <th style={head}>Float</th>
              <th style={head}>Before it</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const preds = predecessorsOf.get(r.id) ?? [];
              const indent = Math.max(0, (r.outline_level ?? 1) - 1) * 11;
              const isCollapsed = collapsed.has(r.id);
              return (
                <tr key={r.id} style={{
                  background: r.is_summary ? T.chip : "transparent",
                  opacity: (r.percent_complete ?? 0) === 100 ? 0.6 : 1,
                }}>
                  {canEdit && (
                    <td style={{ ...cell, width: 1 }}>
                      {!r.is_summary && (
                        <input type="checkbox" checked={picked.has(r.id)}
                          onChange={() => setPicked((was) => {
                            const next = new Set(was);
                            if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                            return next;
                          })} />
                      )}
                    </td>
                  )}
                  <td style={{ ...cell, whiteSpace: "nowrap", color: T.soft, fontSize: 11.5 }}>
                    {r.activity_code ?? "—"}
                  </td>
                  <td style={{ ...cell, minWidth: 260, maxWidth: 420 }}>
                    <div style={{ paddingLeft: indent, display: "flex", gap: 5, alignItems: "baseline" }}>
                      {r.is_summary && (
                        <button onClick={() => setCollapsed((was) => {
                          const next = new Set(was);
                          if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                          return next;
                        })}
                          style={{ border: "none", background: "none", cursor: "pointer",
                                   color: T.navy, padding: 0, fontSize: 11, width: 12 }}>
                          {isCollapsed ? "▸" : "▾"}
                        </button>
                      )}
                      <span style={{
                        fontWeight: r.is_summary ? 700 : 400,
                        color: r.is_summary ? T.navy : T.ink,
                        overflowWrap: "break-word",
                      }}>
                        {canEdit && !r.is_summary
                          ? <InlineEdit value={r.title} type="text" display={r.title}
                              onSave={(next) => change(r, { title: next })} />
                          : r.title}
                      </span>
                      {r.is_milestone && <Chip color={GOLD_INK}>milestone</Chip>}
                    </div>
                  </td>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>
                    {r.is_summary || !canEdit
                      ? <span style={{ color: T.soft }}>{fmtDate(r.start_date)}</span>
                      : <InlineEdit value={r.start_date ?? ""} type="date"
                          display={fmtDate(r.early_start ?? r.start_date)}
                          onSave={(next) => change(r, { start_date: next || null })} />}
                  </td>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>
                    {r.is_summary || !canEdit
                      ? <span style={{ color: T.soft }}>{fmtDate(r.finish_date)}</span>
                      : <InlineEdit value={r.finish_date ?? ""} type="date"
                          display={fmtDate(r.early_finish ?? r.finish_date)}
                          onSave={(next) => change(r, { finish_date: next || null })} />}
                  </td>
                  <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                    {r.is_summary || !canEdit
                      ? <span style={{ color: T.soft }}>{r.percent_complete ?? 0}%</span>
                      : <InlineEdit value={r.percent_complete ?? 0} type="text"
                          display={`${r.percent_complete ?? 0}%`}
                          onSave={(next) => {
                            const n = Math.max(0, Math.min(100, Number(String(next).replace("%", "")) || 0));
                            return change(r, { percent_complete: n });
                          }} />}
                  </td>
                  <td style={{ ...cell }}>
                    {r.is_summary ? null : <Float days={r.total_float} critical={r.is_critical} />}
                  </td>
                  <td style={{ ...cell, fontSize: 11.5, color: T.soft, maxWidth: 220,
                               position: "relative" }}>
                    {r.is_summary ? null : preds.length === 0
                      ? <span style={{ color: GOLD_INK, marginRight: 6 }}>nothing</span>
                      : preds.map((l) => {
                          const from = rows.find((x) => x.id === l.from);
                          return (
                            <div key={l.id} style={{ display: "flex", gap: 4, alignItems: "baseline" }}>
                              <span style={{ overflowWrap: "anywhere" }}>
                                {from?.activity_code ?? "?"} {l.link_type}
                                {l.lag_days ? ` +${l.lag_days}d` : ""}
                              </span>
                              {canEdit && (
                                <button onClick={() => schedule.removeLink(l.id)}
                                  title={`Remove the link from ${from?.title ?? "it"}`}
                                  style={{ border: "none", background: "none", cursor: "pointer",
                                           color: "#8F2B25", padding: 0, fontSize: 11 }}>×</button>
                              )}
                            </div>
                          );
                        })}
                    {canEdit && !r.is_summary && (
                      <button onClick={() => setLinking(linking === r.id ? null : r.id)}
                        title="Add something that has to happen before this"
                        style={{ border: "none", background: "none", cursor: "pointer",
                                 color: T.steel, padding: 0, font: "inherit",
                                 fontSize: 11.5, textDecoration: "underline",
                                 whiteSpace: "nowrap" }}>
                        + add
                      </button>
                    )}
                    {linking === r.id && (
                      <AddPredecessor rows={rows} row={r} onClose={() => setLinking(null)}
                        onPick={async (link) => {
                          const ok = await schedule.addLink(link);
                          if (ok) {
                            setLinking(null);
                            const from = rows.find((x) => x.id === link.from);
                            setNote({ kind: "ok",
                              text: `${r.title.slice(0, 36)} now waits for ${from?.title.slice(0, 36) ?? "it"}.` });
                          }
                        }} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {shown.length === 0 && (
        <Empty text={query ? `Nothing matches "${query}".` : "Nothing in this view."} />
      )}

      <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.7 }}>
        Dates and progress change in the table. Every change saves at once and says
        what it did to the rest of the plan, with an undo.
        <br />
        The date shown is what the logic works out. Where an activity has nothing
        before it, that is the date you typed.
        <br />
        To tie the plan together: add one predecessor at a time with the add link
        in the last column, or tick a run of activities and tie them end to end
        in one go. Filter to <b>Nothing tied to them</b> to find what still needs it.
      </div>
    </Panel>
  );
}
